import { Location } from '@angular/common';
import { Component, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SocialSharing } from '@awesome-cordova-plugins/social-sharing/ngx';
import { AlertController, ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ContactGymAdminComponent } from 'src/components/contact-gym-admin-dialog/contact-gym-admin.component';
import { ActiveGymFacade } from 'src/store/active-gym/active-gym.facade';
import { GymListEntry } from '../../model/gym-list-entry';
import { AuthService } from '../../services/auth.service';
import { shareGymLink } from '../../services/deep-links.service';
import { GymService } from '../../services/gym.service';
import { CreateGymPage } from '../create-gym/create-gym.page';
import { WallsPage } from '../walls/walls.page';


@Component({
  selector: 'app-gym-details-page',
  templateUrl: './gym-details.page.html',
  styleUrls: ['./gym-details.page.scss']
})
export class GymDetailsPage implements OnInit {

  gymId: string;
  gym: GymListEntry;
  isActive: boolean;
  isDownloaded: boolean;
  isAllowedToDeleteGym = false;
  isAllowedToEditGym = false;
  gymAdminsString = '';

  constructor(
    private platform: Platform,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private modalCtrl: ModalController,
    private router: Router,
    private location: Location,
    private gymService: GymService,
    private translate: TranslateService,
    private zone: NgZone,
    private socialSharing: SocialSharing,
    private alertCtrl: AlertController,
    private activeGymFacade: ActiveGymFacade
  ) {
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe(params => {
      this.handleRouteParams(params);
    });
  }

  private async handleRouteParams(params) {
    const gymId = params['gymId'];
    if (gymId) {
      this.gymId = gymId;
    }
    if (this.gymId) {
      await this.loadGymDetails();
    } else {
      this.close();
    }
  }

  private async loadGymDetails() {
    this.gym = null;
    this.gym = await this.loadGym(this.gymId);
    await this.updateDownloadAndActiveState();
    this.isAllowedToDeleteGym = this.authService.isAdmin();
    this.isAllowedToEditGym = this.gym.gymAdmin === this.authService.getCurrentUsername();
    this.gymAdminsString = this.gym.gymAdmin + ((this.gym?.gymAdmins?.length > 0) ? ', ' + this.gym?.gymAdmins?.join(', ') : '');
    // forcing re-render as this page will be triggered by deep-links
    this.zone.run(() => {
    });
  }

  async removeGym() {
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('gym-details-page.delete'),
      message: this.translate.instant('gym-details-page.really-delete-gym'),
      buttons: [{
        text: this.translate.instant('gym-details-page.ok'),
        handler: async () => {
          await this.gymService.removeRemoteGym(this.gym.gymId);
          this.close();
        }
      }],
    });
    await alert.present();
  }

  async onRemove() {
    await this.gymService.deleteGym(this.gym.gymId);
    this.close();
  }

  async editGym() {
    const modal = await this.modalCtrl.create({
      component: CreateGymPage,
      componentProps: { gymToEdit: this.gym },
      id: 'edit-gym-modal'
    }
    );
    modal.onDidDismiss().then(() => {
      this.loadGymDetails();
    });
    await modal.present();
  }

  async activate() {
    await this.activeGymFacade.activeGymChanged(this.gym);
    await this.showBoulders();
  }

  async downloadAndActivate() {
    await this.activate();
  }

  async showBoulders() {
    await this.router.navigate(['/tabs/boulder-list', { refresh: true }]);
  }


  private async updateDownloadAndActiveState() {
    this.isDownloaded = await this.gymService.isLocalGym(this.gym.gymId);
    const activeGymId = await firstValueFrom(this.activeGymFacade.activeGymId$);
    this.isActive = activeGymId === this.gym.gymId;
  }

  close() {
    this.location.back();
  }

  async manageWalls() {
    if (!this.isGymAdmin()) {
      await this.showContactGymAdminDialog();
      return;
    }
    const modal = await this.modalCtrl.create({
      component: WallsPage,
      id: 'WallsPageModal'
    }
    );
    await modal.present();
  }

  private async isGymAdmin() {
    const activeGym = await firstValueFrom(this.activeGymFacade.activeGym$);
    const gym = await this.gymService.getGymListEntryFromPouch$(activeGym.gymId);
    return this.authService.isGymAdmin(gym);
  }

  private async showContactGymAdminDialog() {
    const modal = await this.modalCtrl.create({
      component: ContactGymAdminComponent
    });
    await modal.present();
  }

  private async loadGym(gymId: any): Promise<GymListEntry> {
    if (await this.gymService.isLocalGym(gymId)) {
      return await firstValueFrom(this.gymService.getLocalGym$(gymId));
    } else {
      return await firstValueFrom(this.gymService.getGymListEntry$(gymId));
    }
  }

  async qrCodeClicked() {
    const gymId = this.gym.gymId.replace('gym_info+', '');
    const link = 'https://gym.boulder-book.com/' + gymId;
    await this.socialSharing.share(null, null, null, link);
  }

  async share() {
    const link = shareGymLink(this.gym.gymId);
    if (this.platform.is('mobile')) {
      await this.socialSharing.share(null, null, null, link);
    } else {
      await navigator['clipboard'].writeText(link);
    }
  }
}
